import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { registrationService, getErrorMessage } from '../../../services/apiService';

/**
 * "We read your document — shall we fill these in?"
 *
 * Every document a vendor uploads is read, and what it says is offered to the form: an empty box
 * it can fill, a value it agrees with, one it disagrees with, or one the registry owns and nobody
 * may overwrite. The vendor ticks what they accept and the form fills itself.
 *
 * Nothing is filled in without a tick. A box that already has something in it starts unticked,
 * because overwriting what someone typed should be their decision, not ours.
 *
 * What the server warns about — not the document at all, a certificate that is out of date, a
 * GSTIN, PAN or name that is not the one they verified — sits at the top in the error tone, and a
 * value the registry owns that the document contradicts is a red row that says so. It is only a
 * warning: nothing is disabled. The reader's own confidence is never shown as a number — a
 * percentage under a value reads as a verdict, and it is not one.
 */

const LOW_CONFIDENCE = 80;

// The muted reddish tone the bank panel's error notice uses: wrong at a glance, without shouting.
const ERROR_TONE = 'border-destructive/30 bg-destructive/5 text-destructive';
// The warnings at the top are loud: red border, red text, bold. A block is louder still.
const WARN_TONE = 'border-destructive/70 bg-destructive/10 text-destructive';
const BLOCK_TONE = 'border-destructive bg-destructive/15 text-destructive';

const STATUS = {
  fill: {
    icon: 'Plus',
    label: 'Will fill in',
    row: 'border-primary/30 bg-primary/5',
    chip: 'border-primary/30 bg-primary/10 text-primary',
  },
  conflict: {
    icon: 'AlertTriangle',
    label: 'Differs from what you entered',
    row: 'border-destructive/30 bg-destructive/5',
    chip: ERROR_TONE,
  },
  match: {
    icon: 'Check',
    label: 'Matches',
    row: 'border-border bg-muted/20',
    chip: 'border-success/30 bg-success/5 text-success',
  },
  // The registry owns this value and the document disagrees with it: red, and said plainly.
  locked: {
    icon: 'AlertTriangle',
    label: 'Does not match your verified record',
    row: 'border-destructive/30 bg-destructive/5',
    chip: ERROR_TONE,
  },
};

function FieldRow({ field, checked, onToggle }) {
  const s = STATUS[field.status] || STATUS.match;
  const selectable = field.status === 'fill' || field.status === 'conflict';
  const mismatch = field.status === 'locked';
  return (
    <li className={`flex items-start gap-3 rounded-lg border p-3 ${s.row}`}>
      {selectable ? (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary"
          aria-label={`Fill in ${field.label}`}
        />
      ) : (
        <Icon name={s.icon} size={16} className={`mt-0.5 shrink-0 ${mismatch ? 'text-destructive' : 'text-text-secondary'}`} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-foreground">{field.label}</span>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.chip}`}>
            <Icon name={s.icon} size={10} />
            {s.label}
          </span>
        </div>
        <p className={`mt-1 break-words font-mono text-sm ${mismatch ? 'text-destructive' : 'text-foreground'}`}>
          {mismatch && <span className="mr-1 font-sans text-xs text-text-secondary">On this document:</span>}
          {field.value}
        </p>
        {field.current && field.status !== 'match' && (
          <p className="mt-0.5 break-words text-xs text-text-secondary">
            {mismatch ? 'Verified with GSTN' : 'You entered'}: <span className="font-mono text-foreground">{field.current}</span>
          </p>
        )}
        {mismatch && <p className="mt-1 text-xs text-text-secondary">Verified values stay as they are. If the document is the right one, check the scan is clear.</p>}
      </div>
    </li>
  );
}

/**
 * One line per warning, at the top of the panel, in red. A warning that blocks — a GSTIN or PAN
 * that is not the verified one — wears a "fix this to continue" tag, because Next will not move
 * until the right document is uploaded. Nothing below is ever disabled.
 */
function Warnings({ warnings }) {
  if (!warnings.length) return null;
  const blocking = warnings.some((w) => w.blocks);
  return (
    <div role="alert" className={`mb-3 flex flex-col gap-2 rounded-md border-2 px-3 py-2.5 text-sm font-medium ${blocking ? BLOCK_TONE : WARN_TONE}`}>
      {warnings.map((w) => (
        <p key={`${w.code}:${w.message}`} className="flex items-start gap-2">
          <Icon name={w.blocks ? 'OctagonAlert' : 'AlertTriangle'} size={18} strokeWidth={2.25} className="mt-0.5 shrink-0" />
          <span>
            {w.blocks && (
              <span className="mr-1.5 inline-block rounded bg-destructive px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wide text-white">
                Fix this to continue
              </span>
            )}
            {w.message}
          </span>
        </p>
      ))}
    </div>
  );
}

/**
 * `autoRead` false offers a button instead of reading on sight — for a document something else
 * has already read, so the vendor only pays for a second read if they want the form filled.
 */
export default function DocumentReadPanel({ sessionId, kind, file, onApplied, onCleared, onWarnings, autoRead = true, confirmedBy = '', className = '' }) {
  const [state, setState] = useState({ phase: 'idle', error: null, result: null });
  // The form hears every warning this read produced, so a block can stop Next; the latest handler
  // is kept on a ref so the read effect never closes over a stale one. Gone means no warnings.
  const onWarningsRef = useRef(onWarnings);
  onWarningsRef.current = onWarnings;
  const tell = (warnings) => onWarningsRef.current?.(warnings || []);
  useEffect(() => () => onWarningsRef.current?.([]), []);
  const [accepted, setAccepted] = useState(() => new Set());
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);
  const [asked, setAsked] = useState(false);
  // A reader can turn an 8 into a B. Nothing is written until the vendor says these are right,
  // and the server records who said so — so it stays their statement, not the machine's.
  const [confirmed, setConfirmed] = useState(false);

  // A new file is a new read. No file means the vendor removed it — forget what the old one said.
  useEffect(() => {
    let cancelled = false;
    setApplied(null);
    setAsked(false);
    if (!file || !sessionId || !autoRead) {
      setState({ phase: 'idle', error: null, result: null });
      setAccepted(new Set());
      tell([]);
      return undefined;
    }
    setState({ phase: 'reading', error: null, result: null });
    registrationService
      .readDocument(sessionId, kind, file)
      .then((res) => {
        if (cancelled) return;
        const result = res?.data || null;
        setState({ phase: 'done', error: null, result });
        tell(result?.warnings);
        // Empty boxes are ticked for them; anything that would overwrite their typing is not.
        setAccepted(new Set((result?.fields || []).filter((f) => f.status === 'fill').map((f) => f.key)));
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ phase: 'error', error: getErrorMessage(err), result: null });
        tell([]);
      });
    return () => {
      cancelled = true;
    };
  }, [file, sessionId, kind, autoRead]);

  /** The button path: read now, on request. */
  const readNow = async () => {
    setAsked(true);
    setState({ phase: 'reading', error: null, result: null });
    try {
      const res = await registrationService.readDocument(sessionId, kind, file);
      const result = res?.data || null;
      setState({ phase: 'done', error: null, result });
      tell(result?.warnings);
      setAccepted(new Set((result?.fields || []).filter((f) => f.status === 'fill').map((f) => f.key)));
    } catch (err) {
      setState({ phase: 'error', error: getErrorMessage(err), result: null });
      tell([]);
    }
  };

  const fields = state.result?.fields || [];
  const warnings = state.result?.warnings || [];
  const selectable = useMemo(() => fields.filter((f) => f.status === 'fill' || f.status === 'conflict'), [fields]);
  // Changing what is accepted invalidates the confirmation — they confirmed a different set.
  const toggle = (key) => {
    setConfirmed(false);
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const lowConfidence = useMemo(
    () => fields.filter((f) => accepted.has(f.key) && typeof f.confidence === 'number' && f.confidence < LOW_CONFIDENCE),
    [fields, accepted]
  );

  const apply = async () => {
    setApplying(true);
    try {
      const res = await registrationService.applyDocumentRead(sessionId, kind, [...accepted], { confirmed: true, confirmedBy });
      const data = res?.data;
      setApplied(data?.applied || []);
      if (data?.warnings) tell(data.warnings);
      setState((s) => ({
        ...s,
        result: {
          ...s.result,
          fields: data?.fields || s.result.fields,
          summary: data?.summary || s.result.summary,
          // Measured again after the write: a GSTIN the vendor just accepted no longer disagrees.
          warnings: data?.warnings || s.result.warnings,
        },
      }));
      setAccepted(new Set());
      setConfirmed(false);
      if (onApplied && data?.applied?.length) onApplied(data.applied);
    } catch (err) {
      setState((s) => ({ ...s, error: getErrorMessage(err) }));
    } finally {
      setApplying(false);
    }
  };

  if (state.phase === 'idle') {
    if (!file || !sessionId || autoRead || asked) return null;
    return (
      <Button variant="outline" onClick={readNow} iconName="Wand2" iconPosition="left" className={className}>
        Fill my details in from this document
      </Button>
    );
  }

  if (state.phase === 'reading') {
    return (
      <p className={`flex items-center gap-2 text-sm text-text-secondary ${className}`}>
        <Icon name="Loader2" size={16} className="animate-spin text-primary" />
        Reading your document…
      </p>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className={`rounded-lg border border-warning/40 bg-warning/5 p-3 ${className}`}>
        <p className="flex items-start gap-2 text-sm text-warning">
          <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
          <span>
            {state.error}
            <span className="mt-0.5 block text-xs text-text-secondary">Your document was saved. Fill the details in yourself and carry on.</span>
          </span>
        </p>
        {onCleared && (
          <button type="button" onClick={onCleared} className="mt-2 text-xs font-medium text-primary hover:underline">
            Hide this
          </button>
        )}
      </div>
    );
  }

  const summary = state.result?.summary || { applicable: 0, match: 0, locked: 0 };

  return (
    <div className={`rounded-lg border border-border bg-muted/20 p-3 ${className}`}>
      <Warnings warnings={warnings} />

      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon name="ScanText" size={16} className="text-primary" />
            We read your {String(state.result?.label || 'document').toLowerCase()}
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">
            {summary.applicable > 0
              ? `Tick what you want filled in — ${summary.applicable} field${summary.applicable === 1 ? '' : 's'} we can fill.`
              : summary.locked > 0
                ? 'Nothing to fill in.'
                : 'Everything on it already matches what you entered.'}
            {summary.locked > 0 && (
              <span className="text-destructive">
                {' '}
                {summary.locked === 1 ? '1 value on it does' : `${summary.locked} values on it do`} not match your verified record — see below.
              </span>
            )}
          </p>
        </div>
        {selectable.length > 1 && (
          <button
            type="button"
            onClick={() => setAccepted(accepted.size === selectable.length ? new Set() : new Set(selectable.map((f) => f.key)))}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            {accepted.size === selectable.length ? 'Clear all' : 'Select all'}
          </button>
        )}
      </div>

      <ul className="mt-3 flex flex-col gap-2">
        {fields.map((f) => (
          <FieldRow key={`${f.step}.${f.key}`} field={f} checked={accepted.has(f.key)} onToggle={() => toggle(f.key)} />
        ))}
      </ul>

      {applied && (
        <p className="mt-3 flex items-center gap-2 text-sm text-success">
          <Icon name="CheckCircle2" size={16} />
          {applied.length > 0 ? `Filled in ${applied.length} field${applied.length === 1 ? '' : 's'} for you.` : 'Nothing left to fill in.'}
        </p>
      )}

      {accepted.size > 0 && (
        <div className="mt-3 rounded-lg border border-warning/40 bg-warning/5 p-3">
          <p className="flex items-start gap-2 text-xs text-foreground">
            <Icon name="AlertTriangle" size={14} className="mt-0.5 shrink-0 text-warning" />
            <span>
              <span className="font-medium">Please read these against your document before confirming.</span> Automatic reading is
              not perfect — it can mistake one character for another
              {lowConfidence.length > 0 && (
                <>
                  , and it was unsure about{' '}
                  <span className="font-medium">{lowConfidence.map((f) => f.label).join(', ')}</span>
                </>
              )}
              . Whatever you confirm is recorded against you as correct.
            </span>
          </p>
          <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs font-medium text-foreground">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
            />
            I have checked the {accepted.size} value{accepted.size === 1 ? '' : 's'} above against my document and confirm{' '}
            {accepted.size === 1 ? 'it is' : 'they are'} correct.
          </label>
          <Button
            variant="default"
            onClick={apply}
            loading={applying}
            disabled={applying || !confirmed}
            iconName="Wand2"
            iconPosition="left"
            className="mt-3"
            title={confirmed ? undefined : 'Confirm the values are correct first'}
          >
            Fill in {accepted.size} field{accepted.size === 1 ? '' : 's'}
          </Button>
        </div>
      )}
    </div>
  );
}
